import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export function AuthProvider({ children }) {
  // null = checking, object = authenticated, false = anonymous
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [error, setError] = useState(null);

  const applyMe = useCallback((me) => {
    setUser(me.user);
    setPermissions(me.permissions || []);
    setFeatureFlags(me.feature_flags || {});
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyMe(data);
    } catch (e) {
      // 401 during anonymous mount is expected — don't surface it to the user
      // or the console.
      setUser(false);
      setPermissions([]);
    }
  }, [applyMe]);

  useEffect(() => {
    // If returning from Emergent OAuth callback, let AuthCallback exchange first.
    if (
      typeof window !== "undefined" &&
      window.location.hash?.includes("session_id=")
    ) {
      setUser(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(
    async (email, password) => {
      setError(null);
      try {
        await api.post("/auth/login", { email, password });
        await checkAuth();
        return true;
      } catch (e) {
        setError(formatApiError(e));
        return false;
      }
    },
    [checkAuth],
  );

  const register = useCallback(
    async (payload) => {
      setError(null);
      try {
        await api.post("/auth/register", payload);
        await checkAuth();
        return true;
      } catch (e) {
        setError(formatApiError(e));
        return false;
      }
    },
    [checkAuth],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
    setPermissions([]);
    setFeatureFlags({});
  }, []);

  const startGoogleLogin = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const hasPermission = useCallback(
    (perm) => permissions.includes("*") || permissions.includes(perm),
    [permissions],
  );

  const value = useMemo(
    () => ({
      user,
      permissions,
      featureFlags,
      error,
      isAuthenticated: !!user,
      isChecking: user === null,
      isGuest: user === false,
      isAdmin: user && user.role === "administrator",
      login,
      register,
      logout,
      checkAuth,
      startGoogleLogin,
      hasPermission,
    }),
    [
      user,
      permissions,
      featureFlags,
      error,
      login,
      register,
      logout,
      checkAuth,
      startGoogleLogin,
      hasPermission,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
